import { Component, OnInit, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { PopoverModule } from 'primeng/popover';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';
import { MasterService } from 'src/app/base-classes/master-service';
import { HttpClient } from '@angular/common/http';
import { MenubarModule } from 'primeng/menubar';
import { AuthService } from 'src/app/auth/login/services/auth.service';
import { NotificationApiService } from '../services/notification.service';

interface MenuItem {
  label: string;
  icon?: string;
  routerLink?: string;
  command?: () => void;
  items?: MenuItem[];
}
interface Notification {
  templateName: string;
  templateContent: string;
  time: string;
  isRead: boolean;
}

@Component({
  selector: 'app-top-bar',
  imports: [
    CommonModule,
    RouterModule,
    PopoverModule,
    ButtonModule,
    RippleModule,
    MenubarModule
  ],
  templateUrl: './top-bar.component.html',
  styleUrls: ['./top-bar.component.scss']
})
export class TopBarComponent implements OnInit {
  menuItems: MenuItem[] = [];
  activeMenuItem: string = '';
  isOnCaptureRoute: boolean = false;
  protected service: MasterService<null>;
  userDetails: any = null;
  isSystemUser: boolean = false;
  notifications: Notification[] = [];
  unreadCount: number = 0;
  private readonly LAST_CHECK_KEY = 'tacLastNotificationCheckTime';
  private notificationPollTimer: any = null;
  private notificationApiService = inject(NotificationApiService);

  constructor(
    private router: Router,
    private http: HttpClient,
    private authService: AuthService
  ) {
    this.service = new MasterService<null>(http, '');
    this.userDetails = {
      fullName: this.service.userName,
      email: this.service.userEmail
    };
  }

  ngOnInit(): void {
    this.decodeTokenAndSetUser();
    this.initializeMenuItems();
    this.updateUnreadCount();
    this.setActiveMenuItem();

    // Initialize notification system
    this.initializeNotificationCheckTime();
    const token = localStorage.getItem('authToken');
    if (token) {
      this.registerServiceWorkerAfterLogin();
      this.fetchAndUpdateNotifications();
      this.registerServiceWorkerMessageListeners();
      // Poll for new notifications every 30 seconds
      this.notificationPollTimer = setInterval(() => this.fetchAndUpdateNotifications(), 30000);
    }

    // Listen for route changes to update active menu item and detect capture route
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      this.activeMenuItem = event.url;
      this.isOnCaptureRoute =
        event.url.includes('/scenario/capture/') ||
        event.url.includes('/test-execution/run-tests/') ||
        event.url.includes('/test-studio/recorder-record');
    });

    // Check initial route
    this.isOnCaptureRoute =
      this.router.url.includes('/scenario/capture/') ||
      this.router.url.includes('/test-execution/run-tests/') ||
      this.router.url.includes('/test-studio/recorder-record');
  }

  private decodeTokenAndSetUser(): void {
    const userInfo = this.authService.getUserInfoFromToken();
    if (userInfo) {
      this.isSystemUser = userInfo.isSystemUser;
      this.userDetails = {
        fullName: userInfo.fullName,
        email: userInfo.email
      };
    }
  }

  private initializeMenuItems(): void {
    const allMenuItems: MenuItem[] = [];

    // Only add Admin menu if user is a system user
    if (this.isSystemUser) {
      allMenuItems.push({
        label: 'Admin',
        items: [
          {
            label: 'Client',
            routerLink: '/admin/client',
            command: () => this.navigateToSection('/admin/client')
          },
          {
            label: 'Package Definition',
            items: [
              {
                label: 'Package',
                routerLink: '/packages',
                command: () => this.navigateToSection('/packages')
              },
              {
                label: 'Functional Area',
                routerLink: '/test-suite',
                command: () => this.navigateToSection('/test-suite')
              },
              {
                label: 'Test Scenarios',
                routerLink: '/scenario',
                command: () => this.navigateToSection('/scenario')
              },
            ]
          },
          {
            label: 'User Client Assignment',
            routerLink: '/admin/user-client-mapping',
            command: () => this.navigateToSection('/admin/user-client-mapping')
          },
          {
            label: 'Client Package Assignment',
            routerLink: '/admin/client-package-mapping',
            command: () => this.navigateToSection('/admin/client-package-mapping')
          }
        ]
      });
    }

    // Add remaining menu items (always visible)
    allMenuItems.push(
      {
        label: 'Test Preparation Studio',
        items: [
          {
            label: 'Authentication Profile',
            routerLink: '/admin/auth-profile',
            command: () => this.navigateToSection('/admin/auth-profile')
          },
          {
            label: 'Screen Master',
            routerLink: '/admin/screen-master',
            command: () => this.navigateToSection('/admin/screen-master')
          },
          ...(!this.isSystemUser ? [
            {
              label: 'Functional Area',
              routerLink: '/test-suite',
              command: () => this.navigateToSection('/test-suite')
            },
            {
              label: 'Test Scenarios',
              routerLink: '/scenario',
              command: () => this.navigateToSection('/scenario')
            },
          ] : []),
          {
            label: 'Test Recorder',
            routerLink: '/test-studio/recorder-list',
            command: () => this.navigateToSection('/test-studio/recorder-list')
          },
          {
            label: 'Scenario Templates',
            routerLink: '/admin/skeletons-v2',
            command: () => this.navigateToSection('/admin/skeletons-v2')
          },
          {
            label: 'Scenario Generation',
            routerLink: '/test-studio/data-preparation',
            command: () => this.navigateToSection('/test-studio/data-preparation')
          }
        ]
      },
      {
        label: 'Execution Studio',
        items: [
          {
            label: 'Execute Tests',
            routerLink: '/test-execution',
            command: () => this.navigateToSection('/test-execution')
          },
          {
            label: 'Execution Result',
            routerLink: '/execution-results',
            command: () => this.navigateToSection('/execution-results')
          },
        ]
      },
      {
        label: 'Analytics',
        items: [
          // {
          //   label: 'Execution Report',
          //   routerLink: '/execution-report',
          //   command: () => this.navigateToSection('/execution-report')
          // },
          {
            label: 'Report',
            routerLink: '/report-list',
            command: () => this.navigateToSection('/report-list')
          },
        ]
      },
      {
        label: 'Configuration',
        items: [
          {
            label: 'Scheduler',
            routerLink: '/configuration/scheduler',
            command: () => this.navigateToSection('/configuration/scheduler')
          },
        ]
      }
    );

    this.menuItems = allMenuItems;
  }

  navigateToSection(route: string): void {
    if (!route) {
      return;
    }
    this.activeMenuItem = route;
    this.router.navigate([route]).catch(err => {
    });
  }

  private setActiveMenuItem(): void {
    const currentRoute = this.router.url;
    this.activeMenuItem = currentRoute;
  }

  private updateUnreadCount(): void {
    this.unreadCount = this.notifications.filter(n => !n.isRead).length;
  }

  private initializeNotificationCheckTime(): void {
    if (!localStorage.getItem(this.LAST_CHECK_KEY)) {
      localStorage.setItem(this.LAST_CHECK_KEY, new Date().toISOString());
    }
  }

  private isNewNotification(notification: any): boolean {
    const lastCheckTime = localStorage.getItem(this.LAST_CHECK_KEY);
    if (!lastCheckTime) return false;
    try {
      const notificationTime = new Date(notification.createdOn).getTime();
      const lastCheckTimeMs = new Date(lastCheckTime).getTime();
      return notificationTime > lastCheckTimeMs;
    } catch (e) {
      return false;
    }
  }

  private buildNotificationContent(info: any): string {
    const count = info.scenarioIds?.length || 0;
    return count > 0 ? `${count} scenario(s) executed successfully` : 'Execution completed';
  }

  private async fetchAndUpdateNotifications(): Promise<void> {
    this.notificationApiService.getNotificationList().subscribe({
      next: (response: any[]) => {
        const list = response.map(item => {
          try {
            const additionalInfo = JSON.parse(item.additionalInfo || '{}');
            return {
              ...item,
              id: item.id,
              templateName: additionalInfo?.templateName || 'Execution Completed',
              templateContent: this.buildNotificationContent(additionalInfo),
              time: new Date(item.createdOn).toLocaleString(),
              isRead: item.isRead || false,
              createdOn: item.createdOn,
              additionalInfo: additionalInfo,
            };
          } catch (e) {
            return {
              id: item.id,
              templateName: 'Execution Completed',
              templateContent: 'Execution completed',
              time: new Date(item.createdOn).toLocaleString(),
              isRead: item.isRead || false,
              createdOn: item.createdOn,
              additionalInfo: {},
              ...item
            };
          }
        });

        this.notifications = list;
        this.updateUnreadCount();

        // Show push notifications for new items
        const newNotifications = list.filter(n => this.isNewNotification(n));
        newNotifications.forEach(n => this.showPushNotificationViaSW(n));

        // Update last check time
        localStorage.setItem(this.LAST_CHECK_KEY, new Date().toISOString());
      },
      error: (err) => {
        console.error('Error fetching notifications:', err);
      }
    });
  }

  private showPushNotificationViaSW(notification: any): void {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      const additionalInfo = notification.additionalInfo || {};
      const executionRunId = additionalInfo.executionRunId || '';
      navigator.serviceWorker.controller.postMessage({
        type: 'SHOW_PUSH_NOTIFICATION',
        notification: {
          id: notification.id,
          title: notification.templateName,
          message: notification.templateContent,
          url: `/execution-results?executionRunId=${executionRunId}`,
          ...notification
        }
      });
    }
  }

  private async registerServiceWorkerAfterLogin(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/notification-sw.js');
        if (registration.active) {
          registration.active.postMessage({ type: 'START_BACKGROUND_SYNC' });
        }
      } catch (err) {
        console.error('Service Worker registration failed:', err);
      }
    }
  }

  private registerServiceWorkerMessageListeners(): void {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        const { data } = event;
        if (!data || !data.type) return;

        switch (data.type) {
          case 'FETCH_NOTIFICATION':
            this.fetchAndUpdateNotifications();
            break;
          case 'NOTIFICATION_ACTION':
            if (data.action === 'view') {
              const url = data.notification?.url || '/execution-results';
              this.router.navigateByUrl(url).catch(err => {
                console.error('Navigation error:', err);
              });
            }
            break;
          case 'REQUEST_NOTIFICATION_PERMISSION':
            if (Notification.permission === 'default') {
              Notification.requestPermission();
            }
            break;
          case 'NOTIFICATION_ERROR':
            console.error('Notification error:', data.error);
            break;
        }
      });
    }
  }

  readMore(notification: any, panel: any): void {
    // Mark as read via API
    this.notificationApiService.markAsRead(notification.id).subscribe({
      next: () => {
        notification.isRead = true;
        this.updateUnreadCount();

        // Parse additionalInfo to get executionRunId and scenarioIds
        let additionalInfo: any = {};
        try {
          additionalInfo = typeof notification.additionalInfo === 'string'
            ? JSON.parse(notification.additionalInfo)
            : (notification.additionalInfo || {});
        } catch { additionalInfo = {}; }
        const executionRunId = additionalInfo.executionRunId || '';
        const scenarioIds = additionalInfo.scenarioIds ? additionalInfo.scenarioIds.join(',') : '';

        // Build query parameters
        let queryParams: any = {};
        if (executionRunId) {
          queryParams.executionRunId = executionRunId;
        }
        if (scenarioIds) {
          queryParams.scenarioIds = scenarioIds;
        }
        if (additionalInfo.clientId) {
          queryParams.clientId = additionalInfo.clientId;
        }
        if (additionalInfo.packageId) {
          queryParams.packageId = additionalInfo.packageId;
        }
        if (additionalInfo.functionalAreaId) {
          queryParams.functionalAreaId = additionalInfo.functionalAreaId;
        }
        if (additionalInfo.testTypeId) {
          queryParams.testTypeId = additionalInfo.testTypeId;
        }
        if (additionalInfo.tenantId) {
          queryParams.tenantId = additionalInfo.tenantId;
        }

        // Navigate to execution results with filters
        this.router.navigate(['/execution-results'], { queryParams }).catch(err => {
          console.error('Navigation error:', err);
        });
        panel.hide();
      },
      error: (err) => {
        console.error('Error marking notification as read:', err);
      }
    });
  }

  navigateToAllNotifications(panel: any): void {
    panel.hide();
    this.router.navigate(['/notifications']).catch(err => {
    });
  }

  logout(): void {
    // Stop background sync in service worker
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'STOP_BACKGROUND_SYNC' });
    }

    // Unregister service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          registration.unregister().catch(err => {
            console.error('Service Worker unregistration error:', err);
          });
        });
      });
    }

    // Clear authentication tokens from storage
    localStorage.removeItem('authToken');
    sessionStorage.removeItem('scenario_clientId');
    sessionStorage.removeItem('scenario_functionalAreaId');
    // Navigate to login page
    this.router.navigate(['/auth/login'])
  }
}

declare global {
  interface Window {
    electronAPI?: any;
  }
}